import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { PictureComponent, PictureDisplay } from '@freelancer/ui/picture';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import type { UiConfig } from '@freelancer/ui/ui.interface';

/**
 * A vertical stack used to communicate that there is no content to show yet:
 * an illustration, a header and an optional subheader.
 *
 * For a plain-text subheader, use the `subheaderText` input. For rich content
 * (e.g. links or bold text), project it with the `flEmptyStateSubheader`
 * attribute instead.
 *
 * Project an optional CTA by adding the `flEmptyStateCta` attribute
 * to any element (typically an `fl-button`).
 *
 * The illustration renders everywhere by default. Set `freelancerOnlyImage`
 * when the illustration is Freelancer branded, so it is hidden on
 * Loadshift and Deloitte.
 */
@Component({
  standalone: true,
  imports: [PictureComponent],
  selector: 'fl-empty-state',
  template: `
    @if (!freelancerOnlyImage || !uiConfig.theme) {
      <fl-picture
        [src]="imageSrc"
        [alt]="imageAlt"
        [width]="imageWidth"
        [height]="imageHeight"
        [display]="PictureDisplay.BLOCK"
        [alignCenter]="true"
      />
    }

    <h2 class="EmptyState-header text-title-24">{{ headerText }}</h2>

    @if (subheaderText) {
      <p class="EmptyState-subheader text-body-16">{{ subheaderText }}</p>
    }

    <p
      class="EmptyState-subheader EmptyState-subheader--projected text-body-16"
    >
      <ng-content select="[flEmptyStateSubheader]"></ng-content>
    </p>

    <div class="EmptyState-cta">
      <ng-content select="[flEmptyStateCta]"></ng-content>
    </div>
  `,
  styleUrls: ['./empty-state.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmptyStateComponent {
  protected uiConfig = inject<UiConfig>(UI_CONFIG);

  PictureDisplay = PictureDisplay;

  @Input({ required: true }) imageSrc: string;
  @Input() imageAlt = '';
  @Input() imageWidth?: number;
  @Input() imageHeight?: number;
  /**
   * Hide the illustration on Loadshift and Deloitte.
   * Use this when the illustration is Freelancer branded.
   */
  @Input() freelancerOnlyImage = false;

  @Input({ required: true }) headerText: string;
  @Input() subheaderText?: string;
}
